import { useState, useEffect } from 'react';
import { useTheme, themes } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, Palette, Eye, Paintbrush, RotateCcw, Monitor, Globe, Box, Type, Square, Layers, Sun, Moon } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colorFields = [
  { key: 'primary', label: 'Primary Color', description: 'Main brand color for buttons and links' },
  { key: 'secondary', label: 'Secondary Color', description: 'Accent color for highlights' },
  { key: 'background', label: 'Background Color', description: 'Main page background' },
  { key: 'foreground', label: 'Text Color', description: 'Primary text color' },
  { key: 'muted', label: 'Muted Color', description: 'Subtle backgrounds and borders' },
  { key: 'accent', label: 'Accent Color', description: 'Call-to-action highlights' },
  { key: 'cardBg', label: 'Card Background', description: 'Background for cards and panels' },
  { key: 'cardText', label: 'Card Text Color', description: 'Text color on cards' },
];

// Theme selector component
const ThemeSelector = ({ selectedTheme, onSelect, label }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Object.entries(themes).map(([key, theme]) => (
      <button
        key={key}
        onClick={() => onSelect(key)}
        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
          selectedTheme === key ? 'ring-2 ring-offset-2' : ''
        }`}
        style={{ 
          borderColor: selectedTheme === key ? theme.primary : 'transparent',
          backgroundColor: theme.background,
          ringColor: theme.primary
        }}
        data-testid={`${label}-theme-option-${key}`}
      >
        {selectedTheme === key && (
          <div 
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: theme.primary }}
          >
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
        
        <div className="flex gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: theme.primary }} title="Primary" />
          <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: theme.secondary }} title="Secondary" />
          <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: theme.accent }} title="Accent" />
          <div className="w-8 h-8 rounded-lg border" style={{ backgroundColor: theme.cardBg, borderColor: theme.border }} title="Card" />
        </div>
        
        <h3 className="font-body font-medium" style={{ color: theme.foreground }}>
          {theme.name}
        </h3>
        
        {theme.isDark && (
          <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
            style={{ backgroundColor: theme.muted, color: theme.foreground }}>
            Dark Theme
          </span>
        )}
      </button>
    ))}
  </div>
);

// Custom colors editor component
const CustomColorsEditor = ({ enabled, onToggle, colors, onChange, baseTheme, onReset, label }) => {
  const getEffectiveColor = (key) => {
    if (enabled && colors[key]) {
      return colors[key];
    }
    return baseTheme[key];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${baseTheme.primary}20` }}
          >
            <Paintbrush className="w-6 h-6" style={{ color: baseTheme.primary }} />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium" style={{ color: baseTheme.cardText || baseTheme.foreground }}>
              Custom Colors
            </h3>
            <p className="font-body text-sm opacity-60">
              Override {label} theme colors
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {enabled && (
            <Button variant="outline" size="sm" onClick={onReset} className="gap-2">
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
          )}
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${label}-enable-custom-colors`} />
        </div>
      </div>

      {enabled ? (
        <div className="grid md:grid-cols-2 gap-6">
          {colorFields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label className="font-body font-medium flex items-center justify-between">
                <span>{field.label}</span>
                <span 
                  className="text-xs px-2 py-0.5 rounded"
                  style={{ 
                    backgroundColor: getEffectiveColor(field.key), 
                    color: field.key.includes('Text') || field.key === 'foreground' ? getEffectiveColor('background') : '#fff' 
                  }}
                >
                  {getEffectiveColor(field.key)}
                </span>
              </Label>
              <div className="flex gap-2">
                <div 
                  className="w-12 h-10 rounded-lg border cursor-pointer flex-shrink-0 relative overflow-hidden"
                  style={{ backgroundColor: getEffectiveColor(field.key), borderColor: baseTheme.border }}
                >
                  <input
                    type="color"
                    value={getEffectiveColor(field.key)}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid={`${label}-color-${field.key}`}
                  />
                </div>
                <Input
                  value={colors[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={baseTheme[field.key]}
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs opacity-50">{field.description}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center py-8 opacity-60 font-body">
          Enable custom colors to override the selected theme's color palette.
        </p>
      )}
    </div>
  );
};

export default function AdminThemes() {
  const { 
    currentTheme, activeTheme, changeTheme, 
    headerOpacity, changeHeaderOpacity, 
    customColors, changeCustomColors,
    currentAdminTheme, adminTheme, changeAdminTheme,
    adminCustomColors, changeAdminCustomColors,
    effects3D, change3DEffects, default3DEffects
  } = useTheme();
  
  // Website theme state
  const [selectedTheme, setSelectedTheme] = useState(activeTheme);
  const [selectedOpacity, setSelectedOpacity] = useState(headerOpacity);
  const [enableCustomColors, setEnableCustomColors] = useState(!!customColors);
  const [localCustomColors, setLocalCustomColors] = useState(customColors || {});
  
  // Admin panel theme state
  const [selectedAdminTheme, setSelectedAdminTheme] = useState(adminTheme);
  const [enableAdminCustomColors, setEnableAdminCustomColors] = useState(!!adminCustomColors);
  const [localAdminCustomColors, setLocalAdminCustomColors] = useState(adminCustomColors || {});
  
  // 3D Effects state
  const [local3DEffects, setLocal3DEffects] = useState(effects3D || default3DEffects);
  
  const [saving, setSaving] = useState(false);

  // Sync website theme from context to local state
  useEffect(() => {
    setSelectedTheme(activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    setSelectedOpacity(headerOpacity);
  }, [headerOpacity]);

  useEffect(() => {
    if (customColors) {
      setLocalCustomColors(customColors);
      setEnableCustomColors(true);
    }
  }, [customColors]);

  // Sync admin theme from context to local state
  useEffect(() => {
    setSelectedAdminTheme(adminTheme);
  }, [adminTheme]);

  useEffect(() => {
    if (adminCustomColors) {
      setLocalAdminCustomColors(adminCustomColors);
      setEnableAdminCustomColors(true);
    }
  }, [adminCustomColors]);

  // Sync 3D effects from context
  useEffect(() => {
    if (effects3D) {
      setLocal3DEffects(effects3D);
    }
  }, [effects3D]);

  // Get base themes
  const baseTheme = themes[selectedTheme] || themes.ocean_serenity;
  const baseAdminTheme = themes[selectedAdminTheme] || themes.ocean_serenity;

  const handleColorChange = (key, value) => {
    setLocalCustomColors(prev => ({ ...prev, [key]: value }));
  };

  const handleAdminColorChange = (key, value) => {
    setLocalAdminCustomColors(prev => ({ ...prev, [key]: value }));
  };

  const resetWebsiteColors = () => {
    setLocalCustomColors({});
    setEnableCustomColors(false);
  };

  const resetAdminColors = () => {
    setLocalAdminCustomColors({});
    setEnableAdminCustomColors(false);
  };

  const reset3DEffects = () => {
    setLocal3DEffects(default3DEffects);
  };

  const handle3DEffectChange = (key, value) => {
    setLocal3DEffects(prev => ({ ...prev, [key]: value }));
    // Apply preview immediately
    change3DEffects({ ...local3DEffects, [key]: value });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const websiteColors = enableCustomColors ? localCustomColors : null;
      const adminColors = enableAdminCustomColors ? localAdminCustomColors : null;
      
      await axios.put(`${API}/admin/theme`, {
        active_theme: selectedTheme,
        header_opacity: selectedOpacity,
        custom_colors: websiteColors,
        admin_theme: selectedAdminTheme,
        admin_custom_colors: adminColors,
        effects_3d: local3DEffects
      }, { withCredentials: true });
      
      changeTheme(selectedTheme);
      changeHeaderOpacity(selectedOpacity);
      changeCustomColors(websiteColors);
      changeAdminTheme(selectedAdminTheme);
      changeAdminCustomColors(adminColors);
      change3DEffects(local3DEffects);
      
      toast.success('All theme settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = 
    selectedTheme !== activeTheme || 
    selectedOpacity !== headerOpacity || 
    JSON.stringify(localCustomColors) !== JSON.stringify(customColors || {}) ||
    enableCustomColors !== !!customColors ||
    selectedAdminTheme !== adminTheme ||
    JSON.stringify(localAdminCustomColors) !== JSON.stringify(adminCustomColors || {}) ||
    enableAdminCustomColors !== !!adminCustomColors ||
    JSON.stringify(local3DEffects) !== JSON.stringify(effects3D);

  // Get effective colors for previews
  const getEffectiveColor = (key) => enableCustomColors && localCustomColors[key] ? localCustomColors[key] : baseTheme[key];
  const getAdminEffectiveColor = (key) => enableAdminCustomColors && localAdminCustomColors[key] ? localAdminCustomColors[key] : baseAdminTheme[key];

  return (
    <AdminLayout title="Theme Settings">
      <div className="max-w-5xl">
        <Tabs defaultValue="website" className="space-y-8">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="website" className="gap-2" data-testid="tab-website-theme">
              <Globe className="w-4 h-4" />
              Website Theme
            </TabsTrigger>
            <TabsTrigger value="3d-effects" className="gap-2" data-testid="tab-3d-effects">
              <Box className="w-4 h-4" />
              3D Effects
            </TabsTrigger>
            <TabsTrigger value="admin" className="gap-2" data-testid="tab-admin-theme">
              <Monitor className="w-4 h-4" />
              Admin Theme
            </TabsTrigger>
          </TabsList>

          {/* Website Theme Tab */}
          <TabsContent value="website" className="space-y-8">
            {/* Theme Selection */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                >
                  <Globe className="w-6 h-6" style={{ color: currentAdminTheme.primary }} />
                </div>
                <div>
                  <h2 className="font-display text-xl font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                    Website Theme
                  </h2>
                  <p className="font-body text-sm opacity-60">
                    Choose the theme for your public website
                  </p>
                </div>
              </div>

              <ThemeSelector 
                selectedTheme={selectedTheme} 
                onSelect={setSelectedTheme} 
                label="website"
              />
            </div>

            {/* Custom Colors */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <CustomColorsEditor
                enabled={enableCustomColors}
                onToggle={setEnableCustomColors}
                colors={localCustomColors}
                onChange={handleColorChange}
                baseTheme={baseTheme}
                onReset={resetWebsiteColors}
                label="website"
              />
            </div>

            {/* Header Opacity */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                >
                  <Eye className="w-6 h-6" style={{ color: currentAdminTheme.primary }} />
                </div>
                <div>
                  <h2 className="font-display text-xl font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                    Header Opacity
                  </h2>
                  <p className="font-body text-sm opacity-60">
                    Adjust the transparency of the sticky navigation header
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="font-body font-medium">Opacity Level</Label>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${currentAdminTheme.primary}20`, color: currentAdminTheme.primary }}
                  >
                    {selectedOpacity}%
                  </span>
                </div>
                <Slider
                  value={[selectedOpacity]}
                  onValueChange={(value) => setSelectedOpacity(Math.max(65, value[0]))}
                  min={65}
                  max={100}
                  step={5}
                  className="w-full"
                  data-testid="header-opacity-slider"
                />
                <div className="flex justify-between text-xs opacity-50 font-body">
                  <span>65% (Min)</span>
                  <span>100% (Solid)</span>
                </div>
                
                {/* Live Preview */}
                <div className="mt-6 pt-4 border-t" style={{ borderColor: currentAdminTheme.border }}>
                  <Label className="font-body font-medium mb-3 block">Preview</Label>
                  <div 
                    className="relative h-20 rounded-xl overflow-hidden"
                    style={{ 
                      backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div 
                      className="absolute top-0 left-0 right-0 h-14 flex items-center px-4"
                      style={{ 
                        backgroundColor: getEffectiveColor('background'),
                        opacity: selectedOpacity / 100,
                        backdropFilter: 'blur(12px)'
                      }}
                    />
                    <div className="absolute top-0 left-0 right-0 h-14 flex items-center px-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${getEffectiveColor('primary')}, ${getEffectiveColor('accent')})` }}
                        >
                          <span className="text-white font-bold text-sm">N</span>
                        </div>
                        <span className="font-display text-sm font-medium" style={{ color: getEffectiveColor('foreground') }}>
                          Header Preview
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Website Theme Preview */}
            {(selectedTheme !== activeTheme || enableCustomColors) && (
              <div 
                className="rounded-2xl p-6"
                style={{ backgroundColor: getEffectiveColor('background') }}
              >
                <h3 
                  className="font-display text-2xl font-medium mb-4"
                  style={{ color: getEffectiveColor('foreground') }}
                >
                  Website Theme Preview
                </h3>
                <p 
                  className="font-body mb-4"
                  style={{ color: getEffectiveColor('foreground'), opacity: 0.7 }}
                >
                  This is how your public website will look.
                </p>
                
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: getEffectiveColor('cardBg') }}>
                  <p style={{ color: getEffectiveColor('cardText') }} className="font-body">
                    This is a card component with text.
                  </p>
                </div>
                
                <div className="flex gap-3 flex-wrap">
                  <button className="px-6 py-2 rounded-full font-body font-medium" style={{ backgroundColor: getEffectiveColor('primary'), color: 'white' }}>
                    Primary
                  </button>
                  <button className="px-6 py-2 rounded-full font-body font-medium border-2" style={{ borderColor: getEffectiveColor('primary'), color: getEffectiveColor('primary'), backgroundColor: 'transparent' }}>
                    Secondary
                  </button>
                  <button className="px-6 py-2 rounded-full font-body font-medium" style={{ backgroundColor: getEffectiveColor('secondary'), color: 'white' }}>
                    Accent
                  </button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* 3D Effects Tab */}
          <TabsContent value="3d-effects" className="space-y-8">
            {/* Main Toggle & Intensity */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                  >
                    <Box className="w-6 h-6" style={{ color: currentAdminTheme.primary }} />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                      3D Effects
                    </h2>
                    <p className="font-body text-sm opacity-60">
                      Add depth and dimension to all website elements
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={reset3DEffects} className="gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                  <Switch 
                    checked={local3DEffects.enabled} 
                    onCheckedChange={(v) => handle3DEffectChange('enabled', v)} 
                    data-testid="3d-effects-toggle"
                  />
                </div>
              </div>

              {local3DEffects.enabled && (
                <div className="space-y-6">
                  {/* Master Intensity */}
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <Label className="font-body font-medium">Master Intensity</Label>
                      <span className="text-sm font-medium" style={{ color: currentAdminTheme.primary }}>{local3DEffects.intensity}%</span>
                    </div>
                    <Slider
                      value={[local3DEffects.intensity]}
                      onValueChange={(v) => handle3DEffectChange('intensity', v[0])}
                      max={100}
                      min={0}
                      step={5}
                      className="w-full"
                      data-testid="3d-master-intensity"
                    />
                    <p className="text-xs opacity-50">Controls the overall strength of all 3D effects</p>
                  </div>

                  {/* Shadow Angle */}
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <Label className="font-body font-medium">Light Angle</Label>
                      <span className="text-sm font-medium" style={{ color: currentAdminTheme.primary }}>{local3DEffects.shadowAngle}°</span>
                    </div>
                    <Slider
                      value={[local3DEffects.shadowAngle]}
                      onValueChange={(v) => handle3DEffectChange('shadowAngle', v[0])}
                      max={360}
                      min={0}
                      step={15}
                      className="w-full"
                    />
                    <p className="text-xs opacity-50">Direction of the light source (affects shadow position)</p>
                  </div>

                  {/* Shadow Distance */}
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <Label className="font-body font-medium">Shadow Distance</Label>
                      <span className="text-sm font-medium" style={{ color: currentAdminTheme.primary }}>{local3DEffects.shadowDistance}px</span>
                    </div>
                    <Slider
                      value={[local3DEffects.shadowDistance]}
                      onValueChange={(v) => handle3DEffectChange('shadowDistance', v[0])}
                      max={12}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-xs opacity-50">How far shadows extend from elements</p>
                  </div>
                </div>
              )}
            </div>

            {/* Shadow Colors */}
            {local3DEffects.enabled && (
              <div 
                className="rounded-2xl p-6"
                style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${currentAdminTheme.secondary}20` }}
                  >
                    <Palette className="w-5 h-5" style={{ color: currentAdminTheme.secondary }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                      Shadow Colors
                    </h3>
                    <p className="font-body text-sm opacity-60">
                      Customize shadow and highlight colors
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="font-body font-medium flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      Shadow Color
                    </Label>
                    <div className="flex gap-2">
                      <div 
                        className="w-12 h-10 rounded-lg border cursor-pointer relative overflow-hidden"
                        style={{ backgroundColor: local3DEffects.shadowColor, borderColor: currentAdminTheme.border }}
                      >
                        <input
                          type="color"
                          value={local3DEffects.shadowColor}
                          onChange={(e) => handle3DEffectChange('shadowColor', e.target.value)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <Input
                        value={local3DEffects.shadowColor}
                        onChange={(e) => handle3DEffectChange('shadowColor', e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-body font-medium flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Highlight Color
                    </Label>
                    <div className="flex gap-2">
                      <div 
                        className="w-12 h-10 rounded-lg border cursor-pointer relative overflow-hidden"
                        style={{ backgroundColor: local3DEffects.highlightColor, borderColor: currentAdminTheme.border }}
                      >
                        <input
                          type="color"
                          value={local3DEffects.highlightColor}
                          onChange={(e) => handle3DEffectChange('highlightColor', e.target.value)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <Input
                        value={local3DEffects.highlightColor}
                        onChange={(e) => handle3DEffectChange('highlightColor', e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Element-Specific Controls */}
            {local3DEffects.enabled && (
              <div 
                className="rounded-2xl p-6"
                style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${currentAdminTheme.accent}20` }}
                  >
                    <Layers className="w-5 h-5" style={{ color: currentAdminTheme.accent }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                      Element Controls
                    </h3>
                    <p className="font-body text-sm opacity-60">
                      Fine-tune 3D effects for different elements
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Text Emboss */}
                  <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentAdminTheme.muted }}>
                    <div className="flex items-center gap-3">
                      <Type className="w-5 h-5 opacity-60" />
                      <div>
                        <p className="font-medium">Text Emboss Effect</p>
                        <p className="text-sm opacity-60">Add depth to all text elements</p>
                      </div>
                    </div>
                    <Switch 
                      checked={local3DEffects.enableTextEmboss} 
                      onCheckedChange={(v) => handle3DEffectChange('enableTextEmboss', v)} 
                    />
                  </div>
                  
                  {local3DEffects.enableTextEmboss && (
                    <div className="pl-4 space-y-3">
                      <div className="flex justify-between">
                        <Label className="text-sm">Text Shadow Intensity</Label>
                        <span className="text-sm" style={{ color: currentAdminTheme.primary }}>{local3DEffects.textShadowIntensity}%</span>
                      </div>
                      <Slider
                        value={[local3DEffects.textShadowIntensity]}
                        onValueChange={(v) => handle3DEffectChange('textShadowIntensity', v[0])}
                        max={100}
                        min={0}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Card Depth */}
                  <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentAdminTheme.muted }}>
                    <div className="flex items-center gap-3">
                      <Square className="w-5 h-5 opacity-60" />
                      <div>
                        <p className="font-medium">Card Depth Effect</p>
                        <p className="text-sm opacity-60">Add shadows to cards and panels</p>
                      </div>
                    </div>
                    <Switch 
                      checked={local3DEffects.enableCardDepth} 
                      onCheckedChange={(v) => handle3DEffectChange('enableCardDepth', v)} 
                    />
                  </div>

                  {local3DEffects.enableCardDepth && (
                    <div className="pl-4 space-y-3">
                      <div className="flex justify-between">
                        <Label className="text-sm">Card Shadow Intensity</Label>
                        <span className="text-sm" style={{ color: currentAdminTheme.primary }}>{local3DEffects.cardShadowIntensity}%</span>
                      </div>
                      <Slider
                        value={[local3DEffects.cardShadowIntensity]}
                        onValueChange={(v) => handle3DEffectChange('cardShadowIntensity', v[0])}
                        max={100}
                        min={0}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Button Depth */}
                  <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentAdminTheme.muted }}>
                    <div className="flex items-center gap-3">
                      <Box className="w-5 h-5 opacity-60" />
                      <div>
                        <p className="font-medium">Button Depth Effect</p>
                        <p className="text-sm opacity-60">Add 3D effect to buttons</p>
                      </div>
                    </div>
                    <Switch 
                      checked={local3DEffects.enableButtonDepth} 
                      onCheckedChange={(v) => handle3DEffectChange('enableButtonDepth', v)} 
                    />
                  </div>

                  {local3DEffects.enableButtonDepth && (
                    <div className="pl-4 space-y-3">
                      <div className="flex justify-between">
                        <Label className="text-sm">Button Shadow Intensity</Label>
                        <span className="text-sm" style={{ color: currentAdminTheme.primary }}>{local3DEffects.buttonShadowIntensity}%</span>
                      </div>
                      <Slider
                        value={[local3DEffects.buttonShadowIntensity]}
                        onValueChange={(v) => handle3DEffectChange('buttonShadowIntensity', v[0])}
                        max={100}
                        min={0}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Section Depth */}
                  <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentAdminTheme.muted }}>
                    <div className="flex items-center gap-3">
                      <Layers className="w-5 h-5 opacity-60" />
                      <div>
                        <p className="font-medium">Section Depth Effect</p>
                        <p className="text-sm opacity-60">Add subtle depth to page sections</p>
                      </div>
                    </div>
                    <Switch 
                      checked={local3DEffects.enableSectionDepth} 
                      onCheckedChange={(v) => handle3DEffectChange('enableSectionDepth', v)} 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Live Preview */}
            {local3DEffects.enabled && (
              <div 
                className="rounded-2xl p-6"
                style={{ backgroundColor: getEffectiveColor('background') }}
              >
                <h3 
                  className="font-display text-2xl font-medium mb-4"
                  style={{ color: getEffectiveColor('foreground') }}
                >
                  3D Effects Preview
                </h3>
                <p 
                  className="font-body mb-6"
                  style={{ color: getEffectiveColor('foreground'), opacity: 0.7 }}
                >
                  This preview shows how 3D effects will look on your website.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="rounded-xl p-6 card-3d" style={{ backgroundColor: getEffectiveColor('cardBg') }}>
                    <h4 className="font-display text-xl font-medium mb-2" style={{ color: getEffectiveColor('cardText') }}>
                      Card with 3D Effect
                    </h4>
                    <p style={{ color: getEffectiveColor('cardText'), opacity: 0.7 }} className="font-body text-sm mb-4">
                      This card demonstrates the depth effect with layered shadows.
                    </p>
                    <button 
                      className="px-6 py-2 rounded-full font-body font-medium btn-3d" 
                      style={{ backgroundColor: getEffectiveColor('primary'), color: 'white' }}
                    >
                      3D Button
                    </button>
                  </div>
                  
                  <div className="rounded-xl p-6 card-3d" style={{ backgroundColor: getEffectiveColor('muted') }}>
                    <h4 className="font-display text-xl font-medium mb-2" style={{ color: getEffectiveColor('foreground') }}>
                      Text Emboss
                    </h4>
                    <p style={{ color: getEffectiveColor('foreground') }} className="font-body text-lg">
                      Notice how the text has depth with highlights and shadows.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Admin Panel Theme Tab */}
          <TabsContent value="admin" className="space-y-8">
            {/* Admin Theme Selection */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                >
                  <Monitor className="w-6 h-6" style={{ color: currentAdminTheme.primary }} />
                </div>
                <div>
                  <h2 className="font-display text-xl font-medium" style={{ color: currentAdminTheme.cardText || currentAdminTheme.foreground }}>
                    Admin Panel Theme
                  </h2>
                  <p className="font-body text-sm opacity-60">
                    Choose a separate theme for the admin dashboard
                  </p>
                </div>
              </div>

              <ThemeSelector 
                selectedTheme={selectedAdminTheme} 
                onSelect={setSelectedAdminTheme} 
                label="admin"
              />
            </div>

            {/* Admin Custom Colors */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: currentAdminTheme.cardBg || 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <CustomColorsEditor
                enabled={enableAdminCustomColors}
                onToggle={setEnableAdminCustomColors}
                colors={localAdminCustomColors}
                onChange={handleAdminColorChange}
                baseTheme={baseAdminTheme}
                onReset={resetAdminColors}
                label="admin"
              />
            </div>

            {/* Admin Theme Preview */}
            {(selectedAdminTheme !== adminTheme || enableAdminCustomColors) && (
              <div 
                className="rounded-2xl p-6"
                style={{ backgroundColor: getAdminEffectiveColor('background') }}
              >
                <h3 
                  className="font-display text-2xl font-medium mb-4"
                  style={{ color: getAdminEffectiveColor('foreground') }}
                >
                  Admin Panel Preview
                </h3>
                <p 
                  className="font-body mb-4"
                  style={{ color: getAdminEffectiveColor('foreground'), opacity: 0.7 }}
                >
                  This is how your admin dashboard will look.
                </p>
                
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: getAdminEffectiveColor('cardBg') }}>
                  <p style={{ color: getAdminEffectiveColor('cardText') }} className="font-body">
                    This is a card component in the admin panel.
                  </p>
                </div>
                
                <div className="flex gap-3 flex-wrap">
                  <button className="px-6 py-2 rounded-full font-body font-medium" style={{ backgroundColor: getAdminEffectiveColor('primary'), color: 'white' }}>
                    Primary
                  </button>
                  <button className="px-6 py-2 rounded-full font-body font-medium border-2" style={{ borderColor: getAdminEffectiveColor('primary'), color: getAdminEffectiveColor('primary'), backgroundColor: 'transparent' }}>
                    Secondary
                  </button>
                  <button className="px-6 py-2 rounded-full font-body font-medium" style={{ backgroundColor: getAdminEffectiveColor('secondary'), color: 'white' }}>
                    Accent
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end mt-8">
          <Button
            onClick={handleSaveAll}
            disabled={saving || !hasChanges}
            style={{ backgroundColor: currentAdminTheme.primary }}
            data-testid="save-all-themes-btn"
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
